import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  MapPin,
  Phone,
  Calendar,
  Package,
  User,
  Building,
  Download,
  Image as ImageIcon,
  X,
  AlertCircle,
  Printer,
  MoreVertical,
  Mail,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { orderService } from "../../api/orderService";
import { getStatusColor, getStatusIcon } from "../../utils/statusUtils";
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  status: string;
  pickupStatus: string;
  deliveryStatus: string;
  deliveryCharges?: number | string;
  totalPrice: number;
  deliveryDate: string;
  deliveryTime: string;
  createdAt: string;
  updatedAt: string;
  medicines: Array<{
    name: string;
    potency?: string;
    quantity: number;
    price: number;
  }>;
  notes?: string;
  pharmacyId: string | any; // Can be a string ID or populated object
  pharmacyName: string | any; // Can be a string or populated object
  riderDetail?: {
    name: string;
    phone: string;
    location?: string;
  };
  location: {
    address: string;
    latitude: number;
    longitude: number;
  };
  proofOfDelivery?: {
    beforeImage?: string;
    afterImage?: string;
    signatureImage?: string;
    documents?: Array<{
      name: string;
      fileUrl: string;
      ext?: string;
      uploadedAt: string;
    }>;
  };
  attemptHistory?: Array<{
    attemptNumber: number;
    attemptDate: string;
    proofOfFailure?: {
      fileUrl: string;
      ext: string;
    };
    status: string;
    failureReason?: string;
    notes?: string;
    riderId?: string | any; // Can be a string ID or populated object
  }>;
  pickupOrderId?: string | any; // Can be a string ID or populated object
  signatureSession?: {
    sessionId: string;
    token: string;
    status: "pending" | "completed" | "expired";
    link: string;
    generatedAt: string;
    signatureImage?: string;
    completedAt?: string;
    expiresAt?: string;
    documentsRegeneratedAt?: string;
  };
  documentsRegeneratedAt?: string;
}

const OrderProcessingPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("all");

  // File viewer state
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<{
    url: string;
    type: string;
    name: string;
  } | null>(null);

  // Signature operation loading states
  const [signatureOperations, setSignatureOperations] = useState<{
    [orderId: string]: {
      generatingLink: boolean;
      regeneratingDocs: boolean;
      resettingProcess: boolean;
    };
  }>({});

  // Global loading state for signature operations
  const [isSignatureLoading, setIsSignatureLoading] = useState(false);

  // Reset confirmation modal state
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [orderToReset, setOrderToReset] = useState<Order | null>(null);

  // Filters and pagination per tab
  const [searchTerm, setSearchTerm] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("all");
  const [currentPages, setCurrentPages] = useState({
    all: 1,
    completed: 1,
    cancelled: 1,
    failed: 1,
  });
  const itemsPerPage = 10;

  // Pagination state from backend
  const [backendPagination, setBackendPagination] = useState<{
    [key: string]: {
      total: number;
      totalPages: number;
    };
  }>({});

  // Fetch all orders
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch ALL orders without pagination (limit=1000 to get all)
      const { orders: allOrders } = await orderService.getAllOrders({
        limit: 1000 // Fetch all orders at once for client-side filtering
      });
      setOrders(allOrders);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders");
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely get pharmacy name
  const getPharmacyName = (pharmacyName: any): string => {
    if (typeof pharmacyName === "object" && pharmacyName !== null) {
      return (
        pharmacyName.name || pharmacyName.pharmacyName || "Unknown Pharmacy"
      );
    }
    return pharmacyName || "Unknown Pharmacy";
  };

  // Helper function to safely extract ID from object or string
  const extractId = (value: any): string => {
    if (typeof value === "object" && value !== null) {
      return value._id || value.id || String(value);
    }
    return String(value || "");
  };

  // Helper function to safely get pharmacy phone
  const getPharmacyPhone = (order: any): string => {
    // Check if pharmacyPhone exists directly
    if (order.pharmacyPhone && typeof order.pharmacyPhone === "string") {
      return order.pharmacyPhone;
    }
    // Check if pharmacyId is populated and has phone
    if (typeof order.pharmacyId === "object" && order.pharmacyId?.phone) {
      return order.pharmacyId.phone;
    }
    return "";
  };

  // Get unique pharmacies for filter
  const uniquePharmacies = Array.from(
    new Set(orders.map((order) => getPharmacyName(order.pharmacyName)))
  );

  // Filter orders by tab
  const getFilteredOrders = (tabType: string) => {
    let filtered = orders;

    // Apply tab-specific status filters
    switch (tabType) {
      case "all":
        // pickupStatus === "completed" && deliveryStatus !== "completed" && deliveryStatus !== "cancelled"
        filtered = filtered.filter(
          (order) =>
            order.pickupStatus === "completed" &&
            order.deliveryStatus !== "completed" &&
            order.deliveryStatus !== "cancelled"
        );
        break;
      case "completed":
        // pickupStatus === "completed" && deliveryStatus === "completed"
        filtered = filtered.filter(
          (order) =>
            order.pickupStatus === "completed" &&
            order.deliveryStatus === "completed"
        );
        break;
      case "cancelled":
        // pickupStatus === "completed" && deliveryStatus === "cancelled"
        filtered = filtered.filter(
          (order) =>
            order.pickupStatus === "completed" &&
            order.deliveryStatus === "cancelled"
        );
        break;
      case "failed":
        // pickupStatus === "completed" && deliveryStatus === "failed"
        filtered = filtered.filter(
          (order) =>
            order.pickupStatus === "completed" &&
            order.deliveryStatus === "failed"
        );
        break;
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((order) => {
        const pharmacyName = getPharmacyName(order.pharmacyName);

        return (
          order.orderNumber?.toLowerCase().includes(searchLower) ||
          order.customerName?.toLowerCase().includes(searchLower) ||
          pharmacyName.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply pharmacy filter
    if (pharmacyFilter !== "all") {
      filtered = filtered.filter((order) => {
        const pharmacyName = getPharmacyName(order.pharmacyName);
        return pharmacyName === pharmacyFilter;
      });
    }

    return filtered;
  };

  // Get paginated orders for current tab
  const getPaginatedOrders = (tabType: string) => {
    const filtered = getFilteredOrders(tabType);
    const page = currentPages[tabType as keyof typeof currentPages];
    
    // Use backend pagination info if available, otherwise calculate client-side
    const paginationInfo = backendPagination[tabType] || {
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
    
    // For client-side pagination fallback
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return {
      orders: filtered.slice(startIndex, endIndex),
      total: paginationInfo.total,
      totalPages: paginationInfo.totalPages,
    };
  };

  // Handle page change
  const handlePageChange = (tabType: string, newPage: number) => {
    setCurrentPages((prev) => ({
      ...prev,
      [tabType]: newPage,
    }));
  };

  // Handle status update
  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      setLoading(true);
      await orderService.updateOrderStatus(orderId, newStatus);

      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (err) {
      console.error("Error updating order status:", err);
      toast.error("Failed to update order status");
    } finally {
      setLoading(false);
    }
  };

  // Handle view order details
  const handleViewOrder = (order: Order) => {
    navigate(`/admin/orders/${order._id}`);
  };

  // Handle file/image viewing
  const handleViewFile = (
    fileUrl: string,
    ext: string,
    fileName: string = "file"
  ) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"];
    const pdfExtensions = [".pdf"];

    if (
      imageExtensions.includes(ext.toLowerCase()) ||
      pdfExtensions.includes(ext.toLowerCase())
    ) {
      const fileType = imageExtensions.includes(ext.toLowerCase())
        ? "image"
        : "pdf";
      setViewingFile({ url: fileUrl, type: fileType, name: fileName + ext });
      setFileViewerOpen(true);
    } else {
      // For other files, open in new tab
      window.open(fileUrl, "_blank");
    }
  };

  // Handle file download
  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // Handle file print
  const handlePrintFile = async () => {
    if (!viewingFile) return;

    if (viewingFile.type === "pdf") {
      // For PDFs, create an iframe approach to avoid download
      try {
        // Create a hidden iframe
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = 'none';
        document.body.appendChild(printFrame);

        // Fetch the PDF as blob to avoid download
        const response = await fetch(viewingFile.url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Set iframe source
        printFrame.src = blobUrl;

        // Wait for iframe to load, then print
        printFrame.onload = () => {
          setTimeout(() => {
            try {
              printFrame.contentWindow?.focus();
              printFrame.contentWindow?.print();
              
              // Clean up after print dialog is closed
              setTimeout(() => {
                document.body.removeChild(printFrame);
                URL.revokeObjectURL(blobUrl);
              }, 1000);
            } catch (error) {
              console.error('Print error:', error);
              document.body.removeChild(printFrame);
              URL.revokeObjectURL(blobUrl);
              toast.error("Unable to print. Opening PDF in new tab instead...");
              // Fallback: open in new tab with #toolbar=0 to minimize download prompts
              window.open(viewingFile.url + '#toolbar=0', '_blank');
            }
          }, 1000);
        };

        printFrame.onerror = () => {
          document.body.removeChild(printFrame);
          URL.revokeObjectURL(blobUrl);
          toast.error("Unable to load PDF for printing. Opening in new tab...");
          window.open(viewingFile.url + '#toolbar=0', '_blank');
        };
      } catch (error) {
        console.error('Error preparing PDF for print:', error);
        toast.error("Error loading PDF. Opening in new tab...");
        window.open(viewingFile.url + '#toolbar=0', '_blank');
      }
    } else if (viewingFile.type === "image") {
      // For images, create a print-friendly page
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print - ${viewingFile.name}</title>
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                body {
                  margin: 0;
                  padding: 20px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                }
                img {
                  max-width: 100%;
                  max-height: 100%;
                  height: auto;
                  display: block;
                }
                @media print {
                  body {
                    padding: 0;
                  }
                  img {
                    max-width: 100%;
                    page-break-inside: avoid;
                  }
                }
              </style>
            </head>
            <body>
              <img src="${viewingFile.url}" alt="${viewingFile.name}" onload="setTimeout(() => { window.print(); }, 500);" onerror="document.body.innerHTML='<p>Error loading image</p>'" />
            </body>
          </html>
        `);
        printWindow.document.close();
      } else {
        toast.error("Unable to open print dialog. Please allow pop-ups.");
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setPharmacyFilter("all");
    setCurrentPages({
      all: 1,
      completed: 1,
      cancelled: 1,
      failed: 1,
    });
  };

  // Handle generate signature link
  const handleGenerateSignatureLink = async (order: Order) => {
    try {
      // Set global loading state
      setIsSignatureLoading(true);
      
      // Set loading state
      setSignatureOperations((prev) => ({
        ...prev,
        [order._id]: { ...prev[order._id], generatingLink: true },
      }));

      const result = await orderService.generateSignatureLink(order._id);

      if (result.success) {
        toast.success("Signature link generated and email sent successfully!");
        
        // Refresh orders to get updated signature session
        await fetchOrders();
      } else {
        toast.error(result.message || "Failed to generate signature link");
      }
    } catch (error) {
      console.error("Error generating signature link:", error);
      toast.error("Failed to generate signature link");
    } finally {
      // Clear loading states
      setIsSignatureLoading(false);
      setSignatureOperations((prev) => ({
        ...prev,
        [order._id]: { ...prev[order._id], generatingLink: false },
      }));
    }
  };

  // Handle regenerate documents
  const handleRegenerateDocuments = async (order: Order) => {
    try {
      // Set global loading state
      setIsSignatureLoading(true);
      
      // Set loading state
      setSignatureOperations((prev) => ({
        ...prev,
        [order._id]: { ...prev[order._id], regeneratingDocs: true },
      }));

      const result = await orderService.regenerateDocuments(order._id);

      if (result.success) {
        const documentsGenerated = result.data?.documentsGenerated || 0;
        
        // Always update the order with documentsRegeneratedAt timestamp for successful backend call
        // This ensures the badge persists even on page refresh
        const timestamp = new Date().toISOString();
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o._id === order._id
              ? { ...o, documentsRegeneratedAt: timestamp }
              : o
          )
        );
        
        // Persist the documentsRegeneratedAt timestamp to backend for persistence across refreshes
        try {
          await orderService.updateOrder(order._id, { documentsRegeneratedAt: timestamp });
        } catch (backendUpdateError) {
          console.warn('Failed to update backend with documentsRegeneratedAt timestamp:', backendUpdateError);
          // Continue anyway - the frontend state is updated
        }
        
        // Only show success toast if documents were actually generated
        if (documentsGenerated > 0) {
          toast.success(`${documentsGenerated} documents regenerated successfully!`);
        } else {
          toast.warning("No documents were regenerated. This might be due to upload failures or missing templates.");
        }
      } else {
        toast.error(result.message || "Failed to regenerate documents");
      }
    } catch (error) {
      console.error("Error regenerating documents:", error);
      toast.error("Failed to regenerate documents");
    } finally {
      // Clear loading states
      setIsSignatureLoading(false);
      setSignatureOperations((prev) => ({
        ...prev,
        [order._id]: { ...prev[order._id], regeneratingDocs: false },
      }));
    }
  };

  // Handle reset signature process - open modal
  const handleResetSignatureProcess = (order: Order) => {
    setOrderToReset(order);
    setResetModalOpen(true);
  };

  // Confirm reset signature process
  const confirmResetSignatureProcess = async () => {
    if (!orderToReset) return;

    try {
      // Set global loading state
      setIsSignatureLoading(true);
      
      // Set loading state
      setSignatureOperations((prev) => ({
        ...prev,
        [orderToReset._id]: { ...prev[orderToReset._id], resettingProcess: true },
      }));

      // Make API call to reset signature data on backend
      const result = await orderService.updateOrder(orderToReset._id, {
        signatureSession: null,
        documentsRegeneratedAt: null
      });

      if (result.success) {
        // Reset the order's signature session and document regeneration status in local state
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o._id === orderToReset._id
              ? { 
                  ...o, 
                  signatureSession: null, 
                  documentsRegeneratedAt: null 
                }
              : o
          )
        );
        
        toast.success("Signature process reset successfully! You can now generate a new signature link.");
        
        // Close modal
        setResetModalOpen(false);
        setOrderToReset(null);
      } else {
        toast.error("Failed to reset signature process on server");
      }
      
    } catch (error) {
      console.error("Error resetting signature process:", error);
      toast.error("Failed to reset signature process");
    } finally {
      // Clear loading states
      setIsSignatureLoading(false);
      setSignatureOperations((prev) => ({
        ...prev,
        [orderToReset._id]: { ...prev[orderToReset._id], resettingProcess: false },
      }));
    }
  };

  // Helper to get signature status icon and text
  const getSignatureStatusBadge = (order: Order) => {
    if (!order.signatureSession) return null;

    const { status } = order.signatureSession;

    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
            <Clock className="w-3 h-3 mr-1" />
            Pending Signature
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Signature Received
          </Badge>
        );
      case "expired":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            Link Expired
          </Badge>
        );
      default:
        return null;
    }
  };

  // Helper to get document regeneration status badge
  const getDocumentRegenerationBadge = (order: Order) => {
    if (!order.documentsRegeneratedAt) return null;

    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
        <FileText className="w-3 h-3 mr-1" />
        Document Regenerated
      </Badge>
    );
  };

  // Render order card with accordion
  const renderOrderCard = (
    order: Order,
    showProofOfDelivery = false,
    showProofOfFailure = false
  ) => {
    return (
      <Card key={order._id} className="mb-4">
        {/* Header with order info and 3-dot menu */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-lg">
                        {order.orderNumber}
                      </h3>
                      <Badge className={getStatusColor(order.deliveryStatus)}>
                        {getStatusIcon(order.deliveryStatus)}
                        <span className="ml-1">
                          {order.deliveryStatus?.replace("-", " ")}
                        </span>
                      </Badge>
                  {getSignatureStatusBadge(order)}
                  {getDocumentRegenerationBadge(order)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {order.customerName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {getPharmacyName(order.pharmacyName)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(order.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
            
            {/* 3-dot menu */}
            {showProofOfDelivery && (
              <>
                {/* Debug info */}
                <div className="text-xs text-gray-500 mr-2">
                  Docs: {(order as any).documents?.length || 0} | 
                  POD: {order.proofOfDelivery?.documents?.length || 0}
                </div>
                
                {/* Show dropdown if there are documents in either location */}
                {((order as any).documents && (order as any).documents.length > 0) || 
                 (order.proofOfDelivery?.documents && order.proofOfDelivery.documents.length > 0) ? (
                  <div className="relative">
                  <Button
                      variant="ghost"
                    size="sm"
                      className="h-8 w-8 p-0 hover:bg-gray-100"
                    onClick={(e) => {
                        console.log('3-dot clicked');
                        const button = e.currentTarget as HTMLButtonElement;
                        
                        // Toggle a simple state-based menu
                        const existingMenu = document.getElementById(`menu-${order._id}`);
                        if (existingMenu) {
                          existingMenu.remove();
                        } else {
                          const menu = document.createElement('div');
                          menu.id = `menu-${order._id}`;
                          menu.className = 'fixed bg-white border rounded-lg shadow-lg z-[9999] w-56';
                          
                          // Get button position and set menu position
                          const buttonRect = button.getBoundingClientRect();
                          menu.style.top = `${buttonRect.bottom + 5}px`;
                          menu.style.left = `${buttonRect.right - 200}px`;
                          menu.innerHTML = `
                            <div class="p-1">
                              <button class="w-full text-left px-3 py-2 text-sm rounded flex items-center ${order.signatureSession?.status === "pending" || order.signatureSession?.status === "completed" || signatureOperations[order._id]?.generatingLink ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}" ${order.signatureSession?.status === "pending" || order.signatureSession?.status === "completed" || signatureOperations[order._id]?.generatingLink ? 'disabled' : ''}>
                                ${signatureOperations[order._id]?.generatingLink ? 
                                  '<svg class="mr-2 h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating Link...' :
                                  order.signatureSession?.status === "pending" ?
                                  '<svg class="mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10"></circle><polyline points="12,6 12,12 16,14"></polyline></svg>Link Already Sent' :
                                  order.signatureSession?.status === "completed" ?
                                  '<svg class="mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>Signature Already Received' :
                                  '<svg class="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>Generate Signature Link'
                                }
                              </button>
                              <hr class="my-1">
                              <button class="w-full text-left px-3 py-2 text-sm rounded flex items-center ${order.signatureSession?.status !== "completed" || signatureOperations[order._id]?.regeneratingDocs || order.documentsRegeneratedAt ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'}" ${order.signatureSession?.status !== "completed" || signatureOperations[order._id]?.regeneratingDocs || order.documentsRegeneratedAt ? 'disabled' : ''}>
                                ${signatureOperations[order._id]?.regeneratingDocs ?
                                  '<svg class="mr-2 h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Regenerating...' :
                                  order.documentsRegeneratedAt ?
                                  '<svg class="mr-2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M1 4v6h6M23 20v-6h-6"></path><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>Documents Already Regenerated' :
                                  '<svg class="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M1 4v6h6M23 20v-6h-6"></path><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>Regenerate Documents'
                                }
                              </button>
                              <hr class="my-1">
                              <button class="w-full text-left px-3 py-2 text-sm rounded flex items-center ${signatureOperations[order._id]?.resettingProcess ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}" ${signatureOperations[order._id]?.resettingProcess ? 'disabled' : ''}>
                                ${signatureOperations[order._id]?.resettingProcess ?
                                  '<svg class="mr-2 h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Resetting...' :
                                  '<svg class="mr-2 h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>Reset Signature Process'
                                }
                              </button>
                            </div>
                          `;
                          
                          // Add click handlers
                          const generateBtn = menu.querySelector('button:nth-child(1)');
                          const regenerateBtn = menu.querySelector('button:nth-child(3)');
                          const resetBtn = menu.querySelector('button:nth-child(5)');
                          
                          generateBtn?.addEventListener('click', () => {
                            handleGenerateSignatureLink(order);
                            menu.remove();
                          });
                          
                          regenerateBtn?.addEventListener('click', () => {
                            handleRegenerateDocuments(order);
                            menu.remove();
                          });
                          
                          resetBtn?.addEventListener('click', () => {
                            handleResetSignatureProcess(order);
                            menu.remove();
                          });
                          
                          // Close menu when clicking outside
                          const handleOutsideClick = (e: Event) => {
                            if (!menu.contains(e.target as Node) && e.target !== button) {
                              menu.remove();
                              document.removeEventListener('click', handleOutsideClick);
                            }
                          };
                          
                          setTimeout(() => {
                            document.addEventListener('click', handleOutsideClick);
                          }, 0);
                          
                          document.body.appendChild(menu);
                        }
                      }}
                    >
                      <MoreVertical className="h-4 w-4" />
                  </Button>
              </div>
                ) : (
                  <span className="text-xs text-red-500">No docs found</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Accordion for expandable content */}
        <Accordion type="single" collapsible>
          <AccordionItem value={order._id} className="border-none">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="text-sm text-gray-600">Click to view details</span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="space-y-4">
                {/* Order Information */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-sm font-semibold text-blue-900 mb-3 block">
                    Order Information
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Order Number
                      </Label>
                      <p className="text-base font-mono">{order.orderNumber}</p>
                    </div>
                    {(order as any).priority && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Priority
                        </Label>
                        <Badge variant="outline" className="mt-1">
                          {(order as any).priority}
                        </Badge>
                      </div>
                    )}
                    {(order as any).deliveryAttempts !== undefined && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Delivery Attempts
                        </Label>
                        <p className="text-base">
                          {(order as any).deliveryAttempts} / 3
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Total Price
                      </Label>
                      <p className="text-base font-semibold text-green-600">
                        ${order.totalPrice?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    {order.deliveryCharges !== undefined && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Delivery Charges
                        </Label>
                        <p className="text-base">
                          $
                          {typeof order.deliveryCharges === "number"
                            ? order.deliveryCharges.toFixed(2)
                            : order.deliveryCharges || "0.00"}
                        </p>
                      </div>
                    )}
                    {(order as any).maxAttemptsReached !== undefined && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Max Attempts Reached
                        </Label>
                        <Badge
                          variant={
                            (order as any).maxAttemptsReached
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {(order as any).maxAttemptsReached ? "Yes" : "No"}
                        </Badge>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Created At
                      </Label>
                      <p className="text-sm">{formatDate(order.createdAt)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Last Updated
                      </Label>
                      <p className="text-sm">{formatDate(order.updatedAt)}</p>
                    </div>
                    {(order as any).pickupRequestedAt && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Pickup Requested At
                        </Label>
                        <p className="text-sm">
                          {formatDate((order as any).pickupRequestedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* QR Code */}
                  {(order as any).qrCode && (
                    <div className="mt-4">
                      <Label className="text-sm font-medium text-gray-500 mb-2 block">
                        QR Code
                      </Label>
                      <img
                        src={(order as any).qrCode}
                        alt="Order QR Code"
                        className="w-32 h-32 border rounded-lg cursor-pointer hover:scale-105 transition-transform"
                        onClick={() => {
                          setViewingFile({
                            url: (order as any).qrCode,
                            type: "image",
                            name: `QR-${order.orderNumber}.png`,
                          });
                          setFileViewerOpen(true);
                        }}
                      />
                    </div>
                  )}
                  {/* Documents Selected */}
                  {(order as any).documents &&
                    (order as any).documents.length > 0 && (
                      <div className="mt-4">
                        <Label className="text-sm font-medium text-gray-500 mb-2 block">
                          Documents Required
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {(order as any).documents.map(
                            (doc: string, idx: number) => (
                              <Badge key={idx} variant="secondary">
                                {doc}
                              </Badge>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>

                {/* Customer Information */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="text-sm font-semibold text-gray-900 mb-3 block">
                    Customer Information
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Customer Name
                      </Label>
                      <p className="text-base">{order.customerName}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Customer Phone
                      </Label>
                      <p className="text-base flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {order.customerPhone}
                      </p>
                    </div>
                    {(order as any).customerEmail && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Customer Email
                        </Label>
                        <p className="text-base">
                          {(order as any).customerEmail}
                        </p>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-gray-500">
                        Delivery Address
                      </Label>
                      <p className="text-base flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                        <span>{order.location?.address}</span>
                      </p>
                    </div>
                    {order.location?.latitude != null &&
                      order.location?.longitude != null && (
                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium text-gray-500">
                            Coordinates
                          </Label>
                          <p className="text-sm text-gray-600">
                            Lat: {Number(order.location.latitude).toFixed(6)},
                            Long: {Number(order.location.longitude).toFixed(6)}
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                {/* Pharmacy Information */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Pharmacy Information
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Pharmacy Name
                      </Label>
                      <p className="text-base font-semibold">
                        {getPharmacyName(order.pharmacyName)}
                      </p>
                    </div>
                    {getPharmacyPhone(order) && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Pharmacy Phone
                        </Label>
                        <p className="text-base flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {getPharmacyPhone(order)}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Pharmacy ID
                      </Label>
                      <p className="text-xs font-mono text-gray-600">
                        {extractId(order.pharmacyId)}
                      </p>
                    </div>
                    {(order as any).pickupOrderId && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Pickup Order ID
                        </Label>
                        <p className="text-xs font-mono text-gray-600">
                          {extractId((order as any).pickupOrderId)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rider Information */}
                {order.riderDetail && (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <Label className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Rider Information
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Rider Name
                        </Label>
                        <p className="text-base font-semibold">
                          {order.riderDetail.name}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Rider Phone
                        </Label>
                        <p className="text-base flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {order.riderDetail.phone}
                        </p>
                      </div>
                      {order.riderDetail.location && (
                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium text-gray-500">
                            Rider Location
                          </Label>
                          <p className="text-base flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {order.riderDetail.location}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Delivery Status Information */}
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <Label className="text-sm font-semibold text-cyan-900 mb-3 block">
                    Delivery Status Information
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Pickup Status
                      </Label>
                      <Badge
                        className={getStatusColor(order.pickupStatus)}
                        variant="outline"
                      >
                        {order.pickupStatus?.replace("-", " ")}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Delivery Status
                      </Label>
                      <Badge className={getStatusColor(order.deliveryStatus)}>
                        {getStatusIcon(order.deliveryStatus)}
                        <span className="ml-1">
                          {order.deliveryStatus?.replace("-", " ")}
                        </span>
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Scheduled Delivery Date
                      </Label>
                      <p className="text-base flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {order.deliveryDate
                          ? formatDate(order.deliveryDate)
                          : "Not set"}
                      </p>
                    </div>
                    {(order as any).confirmedAt && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Confirmed At
                        </Label>
                        <p className="text-sm">
                          {formatDate((order as any).confirmedAt)}
                        </p>
                      </div>
                    )}
                    {(order as any).preparedAt && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Prepared At
                        </Label>
                        <p className="text-sm">
                          {formatDate((order as any).preparedAt)}
                        </p>
                      </div>
                    )}
                    {(order as any).pickedUpAt && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Picked Up At
                        </Label>
                        <p className="text-sm">
                          {formatDate((order as any).pickedUpAt)}
                        </p>
                      </div>
                    )}
                    {(order as any).deliveredAt && (
                      <div>
                        <Label className="text-sm font-medium text-gray-500">
                          Delivered At
                        </Label>
                        <p className="text-sm">
                          {formatDate((order as any).deliveredAt)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Medicines */}
                {order.medicines && order.medicines.length > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Label className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Medicines ({order.medicines.length})
                    </Label>
                    <div className="space-y-2">
                      {order.medicines.map((medicine, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border"
                        >
                          <div>
                            <p className="font-semibold">{medicine.name}</p>
                            {medicine.potency && (
                              <p className="text-sm text-gray-500">
                                {medicine.potency}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              Qty: {medicine.quantity}
                            </p>
                            {medicine.price > 0 && (
                              <p className="text-sm text-gray-500">
                                ${medicine.price.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {order.notes && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-sm font-semibold text-blue-900 mb-2 block">
                      Notes
                    </Label>
                    <p className="text-base">{order.notes}</p>
                  </div>
                )}

                {/* Cancellation Reason */}
                {(order as any).cancellationReason && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <Label className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Cancellation Reason
                    </Label>
                    <p className="text-base">
                      {(order as any).cancellationReason}
                    </p>
                  </div>
                )}

                {/* Proof of Delivery Section - Completed Orders */}
                {showProofOfDelivery && order.proofOfDelivery && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Proof of Delivery
                    </Label>

                    {/* Images Section */}
                    {(order.proofOfDelivery.beforeImage ||
                      order.proofOfDelivery.afterImage ||
                      order.proofOfDelivery.signatureImage) && (
                      <div className="mb-4">
                        <Label className="text-xs font-medium text-gray-700 mb-2 block">
                          Delivery Images
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {order.proofOfDelivery.beforeImage && (
                            <div className="p-3 bg-white rounded-lg border">
                              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                                Before Image
                              </Label>
                              <img
                                src={order.proofOfDelivery.beforeImage}
                                alt="Before Delivery"
                                className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity mb-2"
                                onClick={() => {
                                  setViewingFile({
                                    url: order.proofOfDelivery!.beforeImage!,
                                    type: "image",
                                    name: `before-image-${order.orderNumber}.jpg`,
                                  });
                                  setFileViewerOpen(true);
                                }}
                              />
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() => {
                                    setViewingFile({
                                      url: order.proofOfDelivery!.beforeImage!,
                                      type: "image",
                                      name: `before-image-${order.orderNumber}.jpg`,
                                    });
                                    setFileViewerOpen(true);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() =>
                                    handleDownloadFile(
                                      order.proofOfDelivery!.beforeImage!,
                                      `before-image-${order.orderNumber}.jpg`
                                    )
                                  }
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              </div>
                            </div>
                          )}

                          {order.proofOfDelivery.afterImage && (
                            <div className="p-3 bg-white rounded-lg border">
                              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                                After Image
                              </Label>
                              <img
                                src={order.proofOfDelivery.afterImage}
                                alt="After Delivery"
                                className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity mb-2"
                                onClick={() => {
                                  setViewingFile({
                                    url: order.proofOfDelivery!.afterImage!,
                                    type: "image",
                                    name: `after-image-${order.orderNumber}.jpg`,
                                  });
                                  setFileViewerOpen(true);
                                }}
                              />
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() => {
                                    setViewingFile({
                                      url: order.proofOfDelivery!.afterImage!,
                                      type: "image",
                                      name: `after-image-${order.orderNumber}.jpg`,
                                    });
                                    setFileViewerOpen(true);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() =>
                                    handleDownloadFile(
                                      order.proofOfDelivery!.afterImage!,
                                      `after-image-${order.orderNumber}.jpg`
                                    )
                                  }
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              </div>
                            </div>
                          )}

                          {order.proofOfDelivery.signatureImage && (
                            <div className="p-3 bg-white rounded-lg border">
                              <Label className="text-xs font-medium text-gray-600 mb-2 block">
                                Signature
                              </Label>
                              <img
                                src={order.proofOfDelivery.signatureImage}
                                alt="Customer Signature"
                                className="w-full h-32 object-contain rounded cursor-pointer hover:opacity-80 transition-opacity mb-2 bg-gray-50"
                                onClick={() => {
                                  setViewingFile({
                                    url: order.proofOfDelivery!.signatureImage!,
                                    type: "image",
                                    name: `signature-${order.orderNumber}.png`,
                                  });
                                  setFileViewerOpen(true);
                                }}
                              />
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() => {
                                    setViewingFile({
                                      url: order.proofOfDelivery!
                                        .signatureImage!,
                                      type: "image",
                                      name: `signature-${order.orderNumber}.png`,
                                    });
                                    setFileViewerOpen(true);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 text-xs"
                                  onClick={() =>
                                    handleDownloadFile(
                                      order.proofOfDelivery!.signatureImage!,
                                      `signature-${order.orderNumber}.png`
                                    )
                                  }
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Documents Section */}
                    {order.proofOfDelivery.documents &&
                      order.proofOfDelivery.documents.length > 0 && (
                        <div>
                          <Label className="text-xs font-medium text-gray-700 mb-2 block">
                            Documents ({order.proofOfDelivery.documents.length})
                          </Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {order.proofOfDelivery.documents.map(
                              (doc, index) => {
                                // Use ext field if available, otherwise try to extract from URL
                                const ext =
                                  doc.ext ||
                                  doc.fileUrl.substring(
                                    doc.fileUrl.lastIndexOf(".")
                                  ) ||
                                  ".pdf";
                                const isPdf = ext.toLowerCase().includes("pdf");
                                const isImage = [
                                  ".jpg",
                                  ".jpeg",
                                  ".png",
                                  ".gif",
                                  ".webp",
                                  ".svg",
                                ].includes(ext.toLowerCase());

                                return (
                                  <div
                                    key={index}
                                    className="p-3 bg-white rounded-lg border"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-sm font-medium">
                                        {doc.name}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {ext.toUpperCase().replace(".", "")}
                                      </Badge>
                                    </div>

                                    {/* Thumbnail preview for images */}
                                    {isImage && (
                                      <img
                                        src={doc.fileUrl}
                                        alt={doc.name}
                                        className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity mb-2"
                                        onClick={() => {
                                          setViewingFile({
                                            url: doc.fileUrl,
                                            type: "image",
                                            name: `${doc.name}-${order.orderNumber}${ext}`,
                                          });
                                          setFileViewerOpen(true);
                                        }}
                                      />
                                    )}

                                    {/* PDF icon for PDFs */}
                                    {isPdf && (
                                      <div
                                        className="w-full h-24 bg-red-50 rounded mb-2 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                                        onClick={() => {
                                          setViewingFile({
                                            url: doc.fileUrl,
                                            type: "pdf",
                                            name: `${doc.name}-${order.orderNumber}${ext}`,
                                          });
                                          setFileViewerOpen(true);
                                        }}
                                      >
                                        <FileText className="w-12 h-12 text-red-400" />
                                      </div>
                                    )}

                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() =>
                                            handleViewFile(
                                              doc.fileUrl,
                                              ext,
                                              `${doc.name}-${order.orderNumber}`
                                            )
                                          }
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          View
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() =>
                                            handleDownloadFile(
                                              doc.fileUrl,
                                              `${doc.name}-${order.orderNumber}${ext}`
                                            )
                                          }
                                        >
                                          <Download className="w-3 h-3 mr-1" />
                                          Download
                                        </Button>
                                      </div>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => {
                                          setViewingFile({
                                            url: doc.fileUrl,
                                            type: isPdf ? "pdf" : "image",
                                            name: `${doc.name}-${order.orderNumber}${ext}`,
                                          });
                                          setTimeout(
                                            () => handlePrintFile(),
                                            100
                                          );
                                        }}
                                      >
                                        <Printer className="w-3 h-3 mr-1" />
                                        Print
                                      </Button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                      Uploaded: {formatDate(doc.uploadedAt)}
                                    </p>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}

                    {!order.proofOfDelivery.beforeImage &&
                      !order.proofOfDelivery.afterImage &&
                      !order.proofOfDelivery.signatureImage &&
                      (!order.proofOfDelivery.documents ||
                        order.proofOfDelivery.documents.length === 0) && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No proof of delivery uploaded yet
                        </p>
                      )}
                  </div>
                )}

                {/* Delivery Attempt History - Completed Orders (if any failed attempts before success) */}
                {showProofOfDelivery &&
                  order.attemptHistory &&
                  order.attemptHistory.length > 0 && (
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <Label className="text-sm font-semibold text-orange-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Previous Delivery Attempts (
                        {order.attemptHistory.length} attempts before delivery)
                      </Label>
                      <div className="space-y-3">
                        {order.attemptHistory.map((attempt, index) => (
                          <div
                            key={index}
                            className="p-3 bg-white rounded-lg border"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">
                                Attempt #{attempt.attemptNumber}
                              </span>
                              <Badge
                                variant={
                                  attempt.status === "failed"
                                    ? "destructive"
                                    : "default"
                                }
                              >
                                {attempt.status}
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span className="font-medium">Date:</span>{" "}
                                {formatDate(attempt.attemptDate)}
                              </p>
                              {attempt.riderId && (
                                <div className="p-2 bg-gray-50 rounded border mt-2">
                                  <p className="font-medium text-gray-700 mb-1">
                                    Rider Details:
                                  </p>
                                  {typeof attempt.riderId === "object" &&
                                  attempt.riderId !== null ? (
                                    <>
                                      <p className="text-xs">
                                        <span className="font-medium">
                                          Name:
                                        </span>{" "}
                                        {(attempt.riderId as any).name || "N/A"}
                                      </p>
                                      <p className="text-xs">
                                        <span className="font-medium">
                                          Phone:
                                        </span>{" "}
                                        {(attempt.riderId as any).phone ||
                                          "N/A"}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-xs">
                                      <span className="font-medium">
                                        Rider ID:
                                      </span>{" "}
                                      {extractId(attempt.riderId)}
                                    </p>
                                  )}
                                </div>
                              )}
                              {attempt.failureReason && (
                                <p>
                                  <span className="font-medium">Reason:</span>{" "}
                                  {attempt.failureReason}
                                </p>
                              )}
                              {attempt.notes && (
                                <p>
                                  <span className="font-medium">Notes:</span>{" "}
                                  {attempt.notes}
                                </p>
                              )}
                              {attempt.proofOfFailure && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleViewFile(
                                        attempt.proofOfFailure!.fileUrl,
                                        attempt.proofOfFailure!.ext,
                                        `proof-failure-attempt-${attempt.attemptNumber}-${order.orderNumber}`
                                      )
                                    }
                                  >
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleDownloadFile(
                                        attempt.proofOfFailure!.fileUrl,
                                        `proof-failure-attempt-${
                                          attempt.attemptNumber
                                        }-${order.orderNumber}${
                                          attempt.proofOfFailure!.ext
                                        }`
                                      )
                                    }
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Download
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setViewingFile({
                                        url: attempt.proofOfFailure!.fileUrl,
                                        type: attempt
                                          .proofOfFailure!.ext.toLowerCase()
                                          .includes("pdf")
                                          ? "pdf"
                                          : "image",
                                        name: `proof-failure-attempt-${
                                          attempt.attemptNumber
                                        }-${order.orderNumber}${
                                          attempt.proofOfFailure!.ext
                                        }`,
                                      });
                                      setTimeout(() => handlePrintFile(), 100);
                                    }}
                                  >
                                    <Printer className="w-3 h-3 mr-1" />
                                    Print
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Proof of Failure Section - Failed/Cancelled Orders */}
                {showProofOfFailure &&
                  order.attemptHistory &&
                  order.attemptHistory.length > 0 && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <Label className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Delivery Attempt History ({
                          order.attemptHistory.length
                        }{" "}
                        attempts)
                      </Label>
                      <div className="space-y-3">
                        {order.attemptHistory.map((attempt, index) => (
                          <div
                            key={index}
                            className="p-3 bg-white rounded-lg border"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">
                                Attempt #{attempt.attemptNumber}
                              </span>
                              <Badge
                                variant={
                                  attempt.status === "failed"
                                    ? "destructive"
                                    : "default"
                                }
                              >
                                {attempt.status}
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <p>
                                <span className="font-medium">Date:</span>{" "}
                                {formatDate(attempt.attemptDate)}
                              </p>
                              {attempt.riderId && (
                                <div className="p-2 bg-gray-50 rounded border mt-2">
                                  <p className="font-medium text-gray-700 mb-1">
                                    Rider Details:
                                  </p>
                                  {typeof attempt.riderId === "object" &&
                                  attempt.riderId !== null ? (
                                    <>
                                      <p className="text-xs">
                                        <span className="font-medium">
                                          Name:
                                        </span>{" "}
                                        {(attempt.riderId as any).name || "N/A"}
                                      </p>
                                      <p className="text-xs">
                                        <span className="font-medium">
                                          Phone:
                                        </span>{" "}
                                        {(attempt.riderId as any).phone ||
                                          "N/A"}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-xs">
                                      <span className="font-medium">
                                        Rider ID:
                                      </span>{" "}
                                      {extractId(attempt.riderId)}
                                    </p>
                                  )}
                                </div>
                              )}
                              {attempt.failureReason && (
                                <p>
                                  <span className="font-medium">Reason:</span>{" "}
                                  {attempt.failureReason}
                                </p>
                              )}
                              {attempt.notes && (
                                <p>
                                  <span className="font-medium">Notes:</span>{" "}
                                  {attempt.notes}
                                </p>
                              )}
                              {attempt.proofOfFailure && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleViewFile(
                                        attempt.proofOfFailure!.fileUrl,
                                        attempt.proofOfFailure!.ext,
                                        `proof-failure-attempt-${attempt.attemptNumber}-${order.orderNumber}`
                                      )
                                    }
                                  >
                                    <Eye className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleDownloadFile(
                                        attempt.proofOfFailure!.fileUrl,
                                        `proof-failure-attempt-${
                                          attempt.attemptNumber
                                        }-${order.orderNumber}${
                                          attempt.proofOfFailure!.ext
                                        }`
                                      )
                                    }
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Download
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setViewingFile({
                                        url: attempt.proofOfFailure!.fileUrl,
                                        type: attempt
                                          .proofOfFailure!.ext.toLowerCase()
                                          .includes("pdf")
                                          ? "pdf"
                                          : "image",
                                        name: `proof-failure-attempt-${
                                          attempt.attemptNumber
                                        }-${order.orderNumber}${
                                          attempt.proofOfFailure!.ext
                                        }`,
                                      });
                                      setTimeout(() => handlePrintFile(), 100);
                                    }}
                                  >
                                    <Printer className="w-3 h-3 mr-1" />
                                    Print
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Status Update - Only for All Orders tab */}
                {/* {activeTab === "all" && (
                  <div className="flex items-center gap-2 pt-2">
                    <Label className="text-sm font-medium">
                      Update Status:
                    </Label>
                    <Select
                      onValueChange={(value) =>
                        handleStatusUpdate(order._id, value)
                      }
                      value={order.deliveryStatus}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="picked-up">Picked Up</SelectItem>
                        <SelectItem value="in-route">In Route</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )} */}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    );
  };

  // Render tab content
  const renderTabContent = (tabType: string) => {
    const {
      orders: paginatedOrders,
      total,
      totalPages,
    } = getPaginatedOrders(tabType);
    const currentPage = currentPages[tabType as keyof typeof currentPages];

    if (loading && orders.length === 0) {
      return (
        <GlobalLoader 
          isLoading={true} 
          message="Loading orders..." 
          subMessage="Please wait while we fetch your orders" 
        />
      );
    }

    if (paginatedOrders.length === 0) {
      return (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No orders found</p>
        </div>
      );
    }

    const showProofOfDelivery = tabType === "completed";
    const showProofOfFailure = tabType === "cancelled" || tabType === "failed";

    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-600 mb-4">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, total)} of {total} orders
        </div>

        {paginatedOrders.map((order) =>
          renderOrderCard(
            order, 
            showProofOfDelivery, 
            showProofOfFailure || (tabType === 'all' && order.deliveryStatus === 'failed')
          )
        )}

        {/* Pagination - Always show */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <div className="text-sm text-gray-500">
            Page {currentPage} of {totalPages} {totalPages === 1 ? "(All orders shown)" : `(${total} total orders)`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(tabType, currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <div className="text-sm text-gray-500 px-2">
              {currentPage} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(tabType, currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (error && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchOrders}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 mt-1">
            Manage all orders from all pharmacies
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="search" className="mb-3">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="search"
                  placeholder="Order number, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pharmacy" className="mb-3">
                Pharmacy
              </Label>
              <Select value={pharmacyFilter} onValueChange={setPharmacyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Pharmacies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pharmacies</SelectItem>
                  {uniquePharmacies.map((pharmacy) => (
                    <SelectItem key={pharmacy} value={pharmacy}>
                      {pharmacy}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Order Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="all">
                Active Orders ({getFilteredOrders("all").length})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({getFilteredOrders("completed").length})
              </TabsTrigger>
              <TabsTrigger value="cancelled">
                Cancelled ({getFilteredOrders("cancelled").length})
              </TabsTrigger>
              <TabsTrigger value="failed">
                Failed ({getFilteredOrders("failed").length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">{renderTabContent("all")}</TabsContent>

            <TabsContent value="completed">
              {renderTabContent("completed")}
            </TabsContent>

            <TabsContent value="cancelled">
              {renderTabContent("cancelled")}
            </TabsContent>

            <TabsContent value="failed">
              {renderTabContent("failed")}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* File Viewer Modal */}
      <Dialog open={fileViewerOpen} onOpenChange={setFileViewerOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] bg-white overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between mt-6">
              <span className="flex items-center gap-2">
                {viewingFile?.type === "image" ? (
                  <ImageIcon className="w-5 h-5" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
                {viewingFile?.name || "File Preview"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    viewingFile &&
                    handleDownloadFile(viewingFile.url, viewingFile.name)
                  }
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrintFile}>
                  <Printer className="w-4 h-4 mr-1" />
                  Print
                </Button>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFileViewerOpen(false)}
                >
                  <X className="w-4 h-4" />
                </Button> */}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex items-center justify-center p-4 overflow-auto"
            style={{ maxHeight: "calc(95vh - 120px)" }}
          >
            {viewingFile && (
              <>
                {viewingFile.type === "image" ? (
                  <img
                    src={viewingFile.url}
                    alt={viewingFile.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : viewingFile.type === "pdf" ? (
                  <object
                    data={viewingFile.url}
                    type="application/pdf"
                    className="w-full rounded-lg"
                    style={{ minHeight: "600px", height: "calc(95vh - 180px)" }}
                  >
                    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
                      <FileText className="w-16 h-16 text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">Unable to display PDF in browser</p>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          onClick={() => window.open(viewingFile.url, '_blank')}
                        >
                          Open in New Tab
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadFile(viewingFile.url, viewingFile.name)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </object>
                ) : null}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

        {/* Reset Signature Process Confirmation Modal */}
        <Dialog open={resetModalOpen} onOpenChange={setResetModalOpen}>
          <DialogContent className="max-w-lg bg-white border shadow-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Reset Signature Process
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to reset the signature process for order{" "}
              <span className="font-mono font-semibold">
                {orderToReset?.orderNumber}
              </span>
              ?
            </p>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will clear all signature data and allow starting over. 
                The customer will need to sign again.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 px-1">
            <Button
              variant="outline"
              onClick={() => {
                setResetModalOpen(false);
                setOrderToReset(null);
              }}
              disabled={isSignatureLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
              onClick={confirmResetSignatureProcess}
              disabled={isSignatureLoading}
            >
              {isSignatureLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                  Resetting...
                </>
              ) : (
                "Reset Process"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Global loader for signature operations */}
      <GlobalLoader 
        isLoading={isSignatureLoading} 
        message="Processing signature request..." 
        subMessage="Please wait while we generate the signature link or regenerate documents" 
      />
    </div>
  );
};

export default OrderProcessingPage;
